import connectToStores from "alt-utils/lib/connectToStores";
import isEmpty from "lodash/isEmpty";
import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import ReactCSSTransitionGroup from "react-addons-css-transition-group";

import {
    message as messagePropType,
    question as questionPropType,
    response as responsePropType,
} from "./ChatPropTypes";
import Loader from "./Loader";
import MessageList from "./MessageList";
import ResponseSwitcher from "./ResponseSwitcher";
import Footer from "./Footer";

import BrowserNotificationStore from "../../stores/chat/BrowserNotificationStore";
import ChatActions from "../../actions/ChatActions";
import MessageType from "../../constants/ChatMessageTypes";
import ConfigStore from "../../stores/chat/ConfigStore";
import ConfirmationMessageStore from "../../stores/chat/ConfirmationMessageStore";
import QuestionStore from "../../stores/chat/QuestionStore";
import PotentialExpertsStore from "../../stores/chat/PotentialExpertsStore";
import ResponseStore from "../../stores/chat/ResponseStore";
import SingleChildAnimation from "../../utils/SingleChildAnimation";

import chatNavigation, { history } from "../../utils/chat/chatNavigation";
import heartbeat from "../../utils/chat/chatHeartbeat";
import { windowIsVisible } from "../../utils/Utils";
import { triggerAppropriateEvents } from "../../utils/chat/EventHelper";

const heartbeatConfig = {
    onAuthFail: () => {
        history.push("/minimized");
        ChatActions.resetAuth();
    },
    onLoad: ChatActions.load,
    onSuccess: ChatActions.loadSuccess,
    onError: ChatActions.loadFail,
};

export class ChatView extends Component {
    constructor() {
        super();

        this.state = {
            footerHeight: 0,
        };
        this.didUpdateAtLeastOnce = false;
        this.messageListDOM = undefined;

        this.onSetOffsetBottomOfMessageList = this.onSetOffsetBottomOfMessageList.bind(this);
        this.scrollMessageListToBottom = this.scrollMessageListToBottom.bind(this);
        this.setMessageListDOM = this.setMessageListDOM.bind(this);

        this.footerRef = createRef();
    }

    componentWillMount() {
        ChatActions.resetChat();
    }

    componentDidMount() {
        const active = chatNavigation.isUserActive && windowIsVisible();
        const { questionUuid } = this.props.params;
        const { responseId } = this.props.location.query;

        this.props.heartbeat.query({ active, chatId: questionUuid, responseId });
        this.props.heartbeat.start();
    }

    componentWillReceiveProps(nextProps) {
        const questionWillChange = nextProps.params.questionUuid !== this.props.params.questionUuid;
        const responseWillChange = nextProps.location.query.responseId !== this.props.location.query.responseId;

        if (questionWillChange || responseWillChange) {
            ChatActions.resetChat();
            this.props.heartbeat.stop();
            this.props.heartbeat.query({
                active: chatNavigation.isUserActive && windowIsVisible(),
                chatId: nextProps.params.questionUuid,
                responseId: nextProps.location.query.responseId,
            });
            this.props.heartbeat.start();
        }
    }

    componentDidUpdate(prevProps) {
        const { messages, question, response } = this.props;

        const responseChanged = isEmpty(prevProps.response) && !isEmpty(this.props.response);
        const responseLoaded =
            prevProps.isLoadingResponse !== this.props.isLoadingResponse && !this.props.isLoadingResponse;

        if (this.didUpdateAtLeastOnce) {
            triggerAppropriateEvents({
                question,
                messages,
                response,
                prevMessages: prevProps.messages,
                prevQuestion: prevProps.question,
                prevResponse: prevProps.response,
            });
        }

        // prevent re-triggering events on page reload
        if (!this.props.isFirstLoad) {
            this.didUpdateAtLeastOnce = true;
        }

        if (responseChanged || responseLoaded) {
            this.onSetOffsetBottomOfMessageList();
        }
    }

    componentWillUnmount() {
        this.props.heartbeat.stop();
        ChatActions.clearActionBar.defer();
    }

    static getStores() {
        return [
            BrowserNotificationStore,
            ConfigStore,
            ConfirmationMessageStore,
            QuestionStore,
            PotentialExpertsStore,
            ResponseStore,
        ];
    }

    static getPropsFromStores() {
        const response = ResponseStore.getCurrentResponse();

        return {
            autoResponse: ResponseStore.getAutoResponseMessage(),
            confirmationMessageType: ConfirmationMessageStore.getMessageType(),
            disableAskNewQuestionFromUi: ConfigStore.getConfig().disableAskNewQuestionFromUi,
            error: ResponseStore.getState().error,
            hasRejectedAutoResponse: ResponseStore.hasRejectedAutoResponse(),
            hasAcceptedAutoResponse: ResponseStore.hasAcceptedAutoResponse(),
            hasTerminalAutoResponse: ResponseStore.hasTerminalAutoResponse(),
            isAnsweredByExperts: ResponseStore.allResponsesHaveAnswers(),
            isFirstLoad: ResponseStore.getState().isFirstLoad,
            isLoadingExperts: PotentialExpertsStore.getState().isLoading,
            isLoadingResponse: ResponseStore.getState().isLoading,
            lastResponseTimeInMillis: PotentialExpertsStore.getState().lastResponseTimeInMillis,
            latestAnswerForAllResponses: ResponseStore.getLatestAnswerForAllResponses(),
            messages: ResponseStore.getMessagesForCurrentResponse(),
            question: QuestionStore.getQuestion(),
            questionIsRerouted: QuestionStore.questionIsRerouted(),
            response,
            responseIndex: ResponseStore.getIndexOfResponse(response.id),
            responses: ResponseStore.getResponses(),
            shouldDisplayBrowserNotificationPrompt: BrowserNotificationStore.shouldDisplayPrompt(),
            shouldDisplayConfirmationMessage: ConfirmationMessageStore.shouldDisplayMessage(),
            showResponseSwitcher: ResponseStore.getState().showResponseSwitcher,
        };
    }

    onSetOffsetBottomOfMessageList() {
        if (this.footerRef.current) {
            this.setState({ footerHeight: this.footerRef.current.offsetHeight });
        }
    }

    setMessageListDOM(element) {
        this.messageListDOM = element;
    }

    scrollMessageListToBottom() {
        this.messageListDOM.scrollTop = this.messageListDOM.scrollHeight;
    }

    render() {
        const {
            autoRateAcceptedAnswers,
            name: companyName,
            ratingSystem,
            enableRatingAutomatedAnswers,
        } = window.companySettings;
        const {
            autoResponse,
            confirmationMessageType,
            disableAskNewQuestionFromUi,
            error,
            hasRejectedAutoResponse,
            hasAcceptedAutoResponse,
            hasTerminalAutoResponse,
            isAnsweredByExperts,
            isFirstLoad,
            isLoadingExperts,
            isLoadingResponse,
            lastResponseTimeInMillis,
            latestAnswerForAllResponses,
            messages,
            question,
            questionIsRerouted,
            response,
            responses,
            responseIndex,
            shouldDisplayBrowserNotificationPrompt,
            shouldDisplayConfirmationMessage,
            showResponseSwitcher,
        } = this.props;
        const { footerHeight } = this.state;

        return (
            <Loader isLoading={isLoadingResponse || (isFirstLoad && Boolean(error))}>
                <div className="thread-view-container">
                    <ReactCSSTransitionGroup
                        component={SingleChildAnimation}
                        transitionAppear={false}
                        transitionAppearTimeout={0}
                        transitionEnterTimeout={100}
                        transitionLeaveTimeout={80}
                        transitionName="slide"
                    >
                        {showResponseSwitcher && (
                            <ResponseSwitcher
                                currentResponseId={response.id}
                                questionUuid={question.uuid}
                                latestAnswerForAllResponses={latestAnswerForAllResponses}
                                responses={responses}
                            />
                        )}
                    </ReactCSSTransitionGroup>

                    <MessageList
                        autoRateAcceptedAnswers={autoRateAcceptedAnswers}
                        disableAskNewQuestionFromUi={disableAskNewQuestionFromUi}
                        confirmationMessageType={confirmationMessageType}
                        footerHeight={footerHeight}
                        messages={messages}
                        question={question}
                        response={response}
                        responses={responses}
                        responseIndex={responseIndex}
                        scrollToBottom={this.scrollMessageListToBottom}
                        sendMessageListRefToParent={this.setMessageListDOM}
                        shouldDisplayBrowserNotificationPrompt={shouldDisplayBrowserNotificationPrompt}
                        shouldDisplayConfirmationMessage={shouldDisplayConfirmationMessage}
                        messageAlignment={hasTerminalAutoResponse ? "top" : "bottom"}
                    />

                    <Footer
                        autoRateAcceptedAnswers={autoRateAcceptedAnswers}
                        autoResponse={autoResponse}
                        enableRatingAutoResponse={enableRatingAutomatedAnswers}
                        hasRejectedAutoResponse={hasRejectedAutoResponse}
                        hasAcceptedAutoResponse={hasAcceptedAutoResponse}
                        hasTerminalAutoResponse={hasTerminalAutoResponse}
                        isAnsweredByExperts={isAnsweredByExperts}
                        isLoadingExperts={isLoadingExperts}
                        lastResponseTimeInMillis={lastResponseTimeInMillis}
                        companyName={companyName}
                        onSetOffsetBottomOfMessageList={this.onSetOffsetBottomOfMessageList}
                        question={question}
                        questionIsRerouted={questionIsRerouted}
                        ratingSystem={ratingSystem}
                        ref={this.footerRef}
                        response={response}
                        scrollMessageListToBottom={this.scrollMessageListToBottom}
                        shouldDisplayConfirmationMessage={shouldDisplayConfirmationMessage}
                    />
                </div>
            </Loader>
        );
    }
}

ChatView.defaultProps = {
    confirmationMessageType: undefined,
    error: null,
    lastResponseTimeInMillis: null,
    messages: [],
    responses: [],
    shouldDisplayBrowserNotificationPrompt: undefined,
};

ChatView.propTypes = {
    autoResponse: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
    confirmationMessageType: PropTypes.oneOf([
        MessageType.AcceptResponseConfirmationMessage,
        MessageType.AutoResponseConfirmationMessage,
        "",
    ]),
    disableAskNewQuestionFromUi: PropTypes.bool.isRequired,
    error: PropTypes.shape({}),
    hasRejectedAutoResponse: PropTypes.bool.isRequired,
    hasAcceptedAutoResponse: PropTypes.bool.isRequired,
    hasTerminalAutoResponse: PropTypes.bool.isRequired,
    heartbeat: PropTypes.shape({
        query: PropTypes.func.isRequired,
        start: PropTypes.func.isRequired,
        stop: PropTypes.func.isRequired,
    }).isRequired,
    isAnsweredByExperts: PropTypes.bool.isRequired,
    isFirstLoad: PropTypes.bool.isRequired,
    isLoadingExperts: PropTypes.bool.isRequired,
    isLoadingResponse: PropTypes.bool.isRequired,
    lastResponseTimeInMillis: PropTypes.number,
    latestAnswerForAllResponses: responsePropType.isRequired,
    location: PropTypes.shape({
        query: PropTypes.object.isRequired,
    }).isRequired,
    messages: PropTypes.arrayOf(messagePropType),
    params: PropTypes.shape({
        query: PropTypes.shape({
            responseId: PropTypes.number,
        }),
        questionUuid: PropTypes.string.isRequired,
    }).isRequired,
    question: questionPropType.isRequired,
    questionIsRerouted: PropTypes.bool.isRequired,
    response: responsePropType.isRequired,
    responses: PropTypes.arrayOf(responsePropType),
    responseIndex: PropTypes.number.isRequired,
    shouldDisplayBrowserNotificationPrompt: PropTypes.bool,
    shouldDisplayConfirmationMessage: PropTypes.bool.isRequired,
    showResponseSwitcher: PropTypes.bool.isRequired,
};

export default heartbeat("/chat/load", heartbeatConfig)(connectToStores(ChatView));
